import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import InfoIcon from "@mui/icons-material/Info";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import NotificationsActive from "@mui/icons-material/NotificationsActive";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import api from "../api/axios";
import jsPDF from "jspdf";
import LoadingScreen from "../components/LoadingScreen";
import socket from "../socket";
import ReviewDialog from "../components/reviews/ReviewDialog";
import { GoogleMap, useJsApiLoader, Marker, Polyline } from "@react-google-maps/api";
import { fetchRoadRoutePath } from "../utils/routePath";
import { getLatLng, getAddressText } from "../utils/geo";

const colors = {
  deepNavy: "#131921", primary: "#0056c3", primaryContainer: "#1f6feb",
  secondaryContainer: "#fe9800", surfaceBright: "#f7f9fc", surfaceContainerLow: "#f2f4f7",
  surfaceContainer: "#eceef1", surfaceContainerHigh: "#e6e8eb", surfaceContainerLowest: "#ffffff",
  onSurface: "#191c1e", onSurfaceVariant: "#424754", textMuted: "#878787", textMain: "#212121",
  outlineVariant: "#c2c6d6", outline: "#727786", successGreen: "#388E3C", error: "#ba1a1a",
  warningAmber: "#F3A847", onPrimary: "#ffffff", errorContainer: "#ffdad6", onErrorContainer: "#93000a",
  surfaceAlt: "#232F3E",
};

const allTrackingSteps = [
  { label: "Order Placed", key: "ORDER_PLACED" },
  { label: "Order Rejected by Vendor", key: "VENDOR_REJECTED" },
  { label: "Order Accepted by Vendor", key: "VENDOR_ACCEPTED" },
  { label: "Finding Agent for Delivery", key: "LOOKING_FOR_AGENT" },
  { label: "Agent Accepted Delivery", key: "AGENT_ACCEPTED" },
  { label: "Order Being Prepared", key: "VENDOR_PREPARING" },
  { label: "Items Ready for Pickup", key: "VENDOR_READY" },
  { label: "Agent Picked Up Order", key: "PICKED_UP" },
  { label: "Out for Delivery", key: "IN_TRANSIT" },
  { label: "Order Delivered", key: "DELIVERED" },
];

const MAP_LIBRARIES = ["places", "marker", "routes"];

function RouteMap({ pickupAddress, dropoffAddress, pickupCoords, dropoffCoords }) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: MAP_LIBRARIES,
  });

  const [roadPath, setRoadPath] = useState(null);
  const mapRef = useRef(null);
  const [pickup, setPickup] = useState(pickupCoords || null);
  const [dropoff, setDropoff] = useState(dropoffCoords || null);
  const [pendingGeocodes, setPendingGeocodes] = useState(0);
  const geocodeAttemptedRef = useRef({ pickup: false, dropoff: false });

  // Update from direct coords — mark geocode as attempted
  useEffect(() => {
    if (pickupCoords) {
      setPickup(pickupCoords);
      geocodeAttemptedRef.current.pickup = true;
    }
  }, [pickupCoords]);

  useEffect(() => {
    if (dropoffCoords) {
      setDropoff(dropoffCoords);
      geocodeAttemptedRef.current.dropoff = true;
    }
  }, [dropoffCoords]);

  // Geocode address fallback (only if no direct coords provided)
  useEffect(() => {
    if (!isLoaded || !window.google?.maps) return;
    const geocoder = new window.google.maps.Geocoder();

    if (!pickup && pickupAddress && !geocodeAttemptedRef.current.pickup) {
      geocodeAttemptedRef.current.pickup = true;
      setPendingGeocodes(prev => prev + 1);
      geocoder.geocode({ address: pickupAddress }, (results, status) => {
        if (status === "OK" && results?.[0]) {
          const loc = results[0].geometry.location;
          setPickup({ lat: typeof loc.lat === 'function' ? loc.lat() : loc.lat, lng: typeof loc.lng === 'function' ? loc.lng() : loc.lng });
        }
        setPendingGeocodes(prev => Math.max(0, prev - 1));
      });
    }

    if (!dropoff && dropoffAddress && !geocodeAttemptedRef.current.dropoff) {
      geocodeAttemptedRef.current.dropoff = true;
      setPendingGeocodes(prev => prev + 1);
      geocoder.geocode({ address: dropoffAddress }, (results, status) => {
        if (status === "OK" && results?.[0]) {
          const loc = results[0].geometry.location;
          setDropoff({ lat: typeof loc.lat === 'function' ? loc.lat() : loc.lat, lng: typeof loc.lng === 'function' ? loc.lng() : loc.lng });
        }
        setPendingGeocodes(prev => Math.max(0, prev - 1));
      });
    }
  }, [isLoaded, pickupAddress, dropoffAddress, pickup, dropoff]);

  // Fetch route via OSRM
  useEffect(() => {
    let cancelled = false;

    const loadRoute = async () => {
      if (!isLoaded || !window.google?.maps || !pickup || !dropoff || pendingGeocodes > 0) {
        setRoadPath(null);
        return;
      }

      try {
        const path = await fetchRoadRoutePath(pickup, dropoff);
        if (!cancelled) {
          setRoadPath(path);
        }
      } catch (error) {
        if (!cancelled) {
          setRoadPath(null);
        }
      }
    };

    loadRoute();
    return () => { cancelled = true; };
  }, [isLoaded, pickup, dropoff, pendingGeocodes]);

  const fitBounds = useCallback(() => {
    const map = mapRef.current;
    if (!map || !window.google?.maps) return;
    const bounds = new window.google.maps.LatLngBounds();
    if (pickup) bounds.extend(pickup);
    if (dropoff) bounds.extend(dropoff);
    if (!bounds.isEmpty()) map.fitBounds(bounds);
  }, [pickup, dropoff]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    requestAnimationFrame(fitBounds);
  }, [fitBounds]);

  // Re-fit when coordinates change after map is already loaded
  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;
    fitBounds();
  }, [isLoaded, fitBounds]);

  if (loadError) return <RouteMapFallback pickupAddress={pickupAddress} dropoffAddress={dropoffAddress} />;
  if (!isLoaded || pendingGeocodes > 0) return <CircularProgress size={24} />;
  if (!pickup && !dropoff && !pickupAddress && !dropoffAddress) {
    return (
      <Box sx={{ textAlign: "center", py: 2 }}>
        <LocalShippingIcon sx={{ fontSize: 28, color: colors.textMuted }} />
        <Typography variant="caption" sx={{ color: colors.textMuted }}>Route unavailable — no location data</Typography>
      </Box>
    );
  }

  const hasRoute = pickup && dropoff;
  const center = pickup || dropoff || { lat: 20.5937, lng: 78.9629 };

  return (
    <Box sx={{ borderRadius: 1, overflow: "hidden", height: 220, width: "100%" }}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={center}
        zoom={hasRoute ? 13 : 12}
        onLoad={onMapLoad}
        options={{ disableDefaultUI: true, zoomControl: true, mapTypeControl: false, streetViewControl: false }}
      >
        {pickup && <Marker position={pickup} label="P" title="Pickup" />}
        {dropoff && <Marker position={dropoff} label="D" title="Dropoff" />}
        {roadPath && (
          <Polyline
            path={roadPath}
            options={{ strokeColor: colors.primary, strokeWeight: 4, strokeOpacity: 0.8 }}
          />
        )}
      </GoogleMap>
    </Box>
  );
}

function RouteMapFallback({ pickupAddress, dropoffAddress }) {
  if (!pickupAddress && !dropoffAddress) {
    return (
      <Box sx={{ textAlign: "center", py: 2 }}>
        <LocalShippingIcon sx={{ fontSize: 28, color: colors.textMuted }} />
        <Typography variant="caption" sx={{ color: colors.textMuted }}>Route unavailable</Typography>
      </Box>
    );
  }

  const origin = encodeURIComponent(pickupAddress || "unknown");
  const dest = encodeURIComponent(dropoffAddress || "unknown");
  const mapsUrl = `https://www.google.com/maps/dir/${origin}/${dest}`;

  return (
    <Paper
      component="a"
      href={mapsUrl}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        p: 2,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        textDecoration: "none",
        border: "1px dashed",
        borderColor: colors.outlineVariant,
        cursor: "pointer",
        bgcolor: colors.surfaceContainerLow,
        borderRadius: 1,
        "&:hover": { bgcolor: colors.surfaceContainerHigh },
      }}
    >
      <LocalShippingIcon sx={{ fontSize: 28, color: colors.textMuted }} />
      <Typography variant="body2" fontWeight={600} sx={{ color: colors.onSurface }}>
        View Route on Google Maps
      </Typography>
      <Typography variant="caption" sx={{ color: colors.textMuted }}>
        {pickupAddress} → {dropoffAddress}
      </Typography>
    </Paper>
  );
}

const OrderDetailsDashboard = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [isSocketReady, setIsSocketReady] = useState(socket.connected);
  const disconnectTimeoutRef = React.useRef(null);
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  const [editingReviewId, setEditingReviewId] = useState(null);
  // Resolved once at mount — the details page is always opened via a fresh route navigation,
  // so window.location.search reliably carries ?focus= when this component mounts.
  const focusIdRef = React.useRef(new URLSearchParams(window.location.search).get("focus"));

  const order = orders[activeTab] || null;

  const vendor = useMemo(() => {
    if (!order) return null;
    for (const item of order.items || []) {
      if (item?.vendorId && typeof item.vendorId === "object") return item.vendorId;
      if (item?.vendor && typeof item.vendor === "object") return item.vendor;
      if (item?.vendorDetails && typeof item.vendorDetails === "object") return item.vendorDetails;
    }
    if (order?.vendorId && typeof order.vendorId === "object") return order.vendorId;
    if (order?.vendor && typeof order.vendor === "object") return order.vendor;
    if (order?.vendorDetails && typeof order.vendorDetails === "object") return order.vendorDetails;
    return null;
  }, [order]);

  const pickupAddress = useMemo(() => {
    return vendor?.store?.address || vendor?.address || vendor?.store || order?.vendorAddress || order?.vendorAddressData;
  }, [vendor, order]);

  const pickupLocation = useMemo(() => getLatLng(pickupAddress), [pickupAddress]);
  const pickupAddressText = useMemo(() => getAddressText(pickupAddress), [pickupAddress]);

  const dropoffAddress = useMemo(() => {
    return order?.address || order?.shippingAddress || order?.deliveryAddress;
  }, [order]);

  const dropoffLocation = useMemo(() => getLatLng(dropoffAddress), [dropoffAddress]);
  const dropoffAddressText = useMemo(() => getAddressText(dropoffAddress), [dropoffAddress]);

  const handleOpenReview = async () => {
    const firstItem = order?.items?.[0];
    const productId = firstItem?.productId || firstItem?._id || firstItem?.id;
    if (!productId || !order?._id) {
      setEditingReviewId(null);
      setOpenReviewDialog(true);
      return;
    }

    try {
      const res = await api.get(`/reviews/eligibility/${productId}/${order._id}`);
      if (res.data?.mode === "EDIT" || res.data?.reviewId) {
        setEditingReviewId(res.data.reviewId || null);
      } else {
        setEditingReviewId(null);
      }
    } catch (err) {
      setEditingReviewId(null);
    }

    setOpenReviewDialog(true);
  };

  // 📡 Socket Setup
  useEffect(() => {
    socket.emit("join_order", orderId);

    const handleOrderUpdate = (data) => {
      if (data.orderId === orderId) {
        setOrders((prev) => prev.map((o) =>
          o._id === data.orderId
            ? { ...o, status: data.status || o.status, tracking: data.tracking || o.tracking, agent: data.agent ? { ...o.agent, ...data.agent } : o.agent, agentPhase: data.agentPhase || o.agentPhase, deliveryPhase: data.deliveryPhase || o.deliveryPhase }
            : o
        ));
        setNotification({ type: "success", message: data.message || "Order status updated" });
        setTimeout(() => setNotification(null), 3000);
      }
    };

    const handleConnect = () => {
      if (disconnectTimeoutRef.current) {
        clearTimeout(disconnectTimeoutRef.current);
        disconnectTimeoutRef.current = null;
      }
      setIsSocketReady(true);
      setNotification((prev) => prev?.type === "warning" ? { type: "success", message: "Connection restored" } : null);
      setTimeout(() => setNotification(null), 2000);
      socket.emit("join_order", orderId);
    };

    const handleDisconnect = () => {
      disconnectTimeoutRef.current = setTimeout(() => {
        setIsSocketReady(false);
        setNotification({ type: "warning", message: "Connection lost - reconnecting..." });
      }, 2000);
    };

    const handleConnectError = () => {
      setNotification({ type: "error", message: "Connection error - updates may be delayed" });
    };

    const handleOrderCancelled = (data) => {
      if (data.orderId === orderId) {
        setOrders((prev) => prev.map((o) =>
          o._id === data.orderId
            ? { ...o, status: data.status, tracking: data.tracking }
            : o
        ));
        setNotification({ type: "error", message: data.message || "Order has been cancelled by the vendor" });
      }
    };

    socket.on("vendor_order_updated", handleOrderUpdate);
    socket.on("order_status_updated", handleOrderUpdate);
    socket.on("vendor_accepted", handleOrderUpdate);
    socket.on("vendor_preparing", handleOrderUpdate);
    socket.on("vendor_ready", handleOrderUpdate);
    socket.on("vendor_rejected", handleOrderUpdate);
    socket.on("order_cancelled", handleOrderCancelled);
    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("connect_error", handleConnectError);

    return () => {
      if (disconnectTimeoutRef.current) clearTimeout(disconnectTimeoutRef.current);
      socket.off("vendor_order_updated", handleOrderUpdate);
      socket.off("order_status_updated", handleOrderUpdate);
      socket.off("vendor_accepted", handleOrderUpdate);
      socket.off("vendor_preparing", handleOrderUpdate);
      socket.off("vendor_ready", handleOrderUpdate);
      socket.off("vendor_rejected", handleOrderUpdate);
      socket.off("order_cancelled", handleOrderCancelled);
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleConnectError);
    };
  }, [orderId]);

  // 📦 Fetch order(s) on mount
  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const res = await api.get(`/orders/${orderId}`);
        const orderData = res.data.order;
        if (orderData.userId) {
          socket.emit("customer_join", orderData.userId);
        }

        // Resolve orders: either from group or single
        let resolvedOrders;
        if (orderData.groupId) {
          const groupRes = await api.get(`/orders/group/${orderData.groupId}`);
          resolvedOrders = groupRes.data.orders || [orderData];
        } else {
          resolvedOrders = [orderData];
        }

        // Split every resolved order with multiple items into per-product pseudo-orders for tab display.
        // (Previously this only ran when the whole group was a single order, so multi-item member
        // orders of a group hid their other items and the next group order appeared as a "second product".)
        const expandedOrders = [];
        for (const ord of resolvedOrders) {
          const items = ord.items || [];
          if (items.length > 1) {
            for (const item of items) {
              expandedOrders.push({ ...ord, items: [item], total: item.price * item.qty });
            }
          } else {
            expandedOrders.push(ord);
          }
        }
        resolvedOrders = expandedOrders;

        // If vendorId is a string (not populated), fetch vendor data separately
        const vendorIds = new Set();
        for (const order of resolvedOrders) {
          for (const item of order.items || []) {
            const vid = item.vendorId;
            if (vid && typeof vid === "string") vendorIds.add(vid);
          }
        }
        if (vendorIds.size > 0) {
          try {
            const firstVendorId = Array.from(vendorIds)[0];
            const vendorRes = await api.get(`/vendor/${firstVendorId}/store`);
            const vendorData = vendorRes.data?.vendor;
            if (vendorData) {
              for (const order of resolvedOrders) {
                for (const item of order.items || []) {
                  if (typeof item.vendorId === "string" && item.vendorId === vendorData._id) {
                    item.vendorId = vendorData;
                  }
                }
              }
            }
          } catch (e) {
            // Vendor fetch failed — map will show dropoff only
          }
        }

        setOrders(resolvedOrders);
      } catch (err) {
        console.error("Failed to load order:", err);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  // Set active tab based on focus query param after orders are loaded
  useEffect(() => {
    if (!orders.length) return;
    const focusId = focusIdRef.current;
    if (focusId && orders.length > 1) {
      const focusIndex = orders.findIndex(o => o.items?.[0]?.productId?.toString() === focusId);
      if (focusIndex >= 0) {
        setActiveTab(focusIndex);
      }
    }
  }, [orders]);

  if (loading) {
    return <LoadingScreen message="Loading order details..." />;
  }

  if (!orders.length) {
    return (
      <Paper sx={{ p: 4, textAlign: "center", borderRadius: 1 }}>
        <Typography sx={{ color: colors.textMuted }}>Order not found</Typography>
      </Paper>
    );
  }

  const isMultiVendor = orders.length > 1;
  const firstItem = order?.items?.[0];
  const lastTracking = order?.tracking?.at(-1);
  const isDelivered = lastTracking?.label === "Order Delivered";
  const isCancelled = order?.tracking?.some(t => t.key === "CANCELLED");
  const isRejected = order?.tracking?.some(t => t.key === "VENDOR_REJECTED");

  const downloadInvoice = () => {
    const pdf = new jsPDF();
    pdf.text("INVOICE", 20, 20);
    pdf.text(`Order ID: ${order._id}`, 20, 30);
    pdf.text(`Date: ${new Date(order.createdAt).toLocaleDateString()}`, 20, 40);
    pdf.text(`Payment: ${order.paymentMethod || "N/A"}`, 20, 50);
    pdf.save(`Invoice_${order._id}.pdf`);
  };

  return (
    <>
    <Box>
      {notification && (
        <Alert severity={notification.type} sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
          onClose={() => setNotification(null)}
          icon={notification.type === "info" ? <NotificationsActive /> : undefined}>
          {notification.message}
          {!isSocketReady && notification.type === "warning" && <CircularProgress size={18} sx={{ ml: 1 }} />}
        </Alert>
      )}

      {!isSocketReady && (
        <Alert severity="warning" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={18} /> Reconnecting to real-time updates...
        </Alert>
      )}

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 1, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", border: "1px solid", borderColor: colors.outlineVariant }}>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, justifyContent: "space-between", alignItems: { md: "flex-end" }, gap: 2 }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 22, color: colors.onSurface, mb: 1 }}>
              Order Details
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 11, color: colors.textMuted, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Order ID: <Box component="span" sx={{ fontWeight: 600, fontSize: 14, color: colors.onSurface, textTransform: "none", letterSpacing: "normal" }}>{order._id}</Box>
              </Typography>
              <Typography sx={{ fontSize: 14, color: colors.outlineVariant }}>|</Typography>
              <Typography sx={{ fontSize: 11, color: colors.textMuted, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Order Date: <Box component="span" sx={{ fontWeight: 600, fontSize: 14, color: colors.onSurface, textTransform: "none", letterSpacing: "normal" }}>
                  {new Date(order.createdAt).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
                </Box>
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button variant="outlined" onClick={downloadInvoice} sx={{ borderColor: colors.outlineVariant, color: colors.onSurface, textTransform: "none", fontWeight: 600, fontSize: 13 }}>
              <ReceiptLongIcon sx={{ fontSize: 16, mr: 0.5 }} /> Download Invoice
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Multi-vendor Tabs */}
      {isMultiVendor && (
        <Paper sx={{ mb: 3, borderRadius: 1, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", border: "1px solid", borderColor: colors.outlineVariant }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
            {orders.map((o, i) => (
              <Tab key={`${o._id}-${i}`} label={`Product ${i + 1}`} />
            ))}
          </Tabs>
        </Paper>
      )}

      {/* Bento Grid Layout */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" }, gap: 3 }}>
        {/* Left Column */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Product Card */}
          <Paper sx={{ borderRadius: 1, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", border: "1px solid", borderColor: colors.outlineVariant }}>
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: "flex", gap: 3 }}>
                <Box sx={{ width: { xs: 100, md: 140 }, height: { xs: 100, md: 140 }, flexShrink: 0, bgcolor: colors.surfaceContainerLow, borderRadius: 1, border: "1px solid", borderColor: colors.outlineVariant, display: "flex", alignItems: "center", justifyContent: "center", p: 1 }}>
                  {firstItem?.imageUrl ? (
                    <Box component="img" src={firstItem.imageUrl} alt={firstItem.title} sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
                  ) : (
                    <Typography sx={{ fontSize: 11, color: colors.textMuted }}>No Image</Typography>
                  )}
                </Box>
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Typography sx={{ fontWeight: 600, fontSize: { xs: 14, md: 16 }, color: colors.onSurface, maxWidth: { xs: 200, md: 400 } }}>
                      {firstItem?.title || "Order Items"}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: 18, color: colors.onSurface, whiteSpace: "nowrap", ml: 2 }}>
                      ₹{Number((firstItem?.price || 0) * (firstItem?.qty || 1)).toLocaleString("en-IN")}
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Typography sx={{ fontSize: 14, color: colors.textMuted }}>
                      Quantity: <Box component="span" sx={{ color: colors.onSurface, fontWeight: 600 }}>{firstItem?.qty || 1}</Box>
                    </Typography>
                  </Box>
                  {isDelivered && (
                    <Box sx={{ mt: "auto", pt: 2, display: "flex", gap: 2 }}>
                      <Button sx={{ color: colors.primary, fontSize: 13, fontWeight: 600, textTransform: "none", p: 0, minWidth: "auto", "&:hover": { textDecoration: "underline" } }}
                        onClick={handleOpenReview}>Write / Edit Review</Button>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
            <Box sx={{ bgcolor: colors.surfaceContainerLow, px: 3, py: 2, borderTop: "1px solid", borderColor: colors.outlineVariant, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LocalShippingIcon sx={{ color: colors.primary, fontSize: 20 }} />
                <Typography sx={{ fontSize: 14, color: colors.onSurface }}>
                  {isDelivered ? "Delivered" : "Standard Delivery"}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Tracking Timeline */}
          <Paper sx={{ p: 3, borderRadius: 1, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", border: "1px solid", borderColor: colors.outlineVariant }}>
            <Typography sx={{ fontWeight: 600, fontSize: 16, color: colors.onSurface, mb: 3 }}>
              Tracking Timeline
            </Typography>
              <Box sx={{ position: "relative" }}>
                {(() => {
                  const isTerminated = order.tracking?.some(t => ["VENDOR_REJECTED", "CANCELLED"].includes(t.key));
                  const completedCount = allTrackingSteps.filter(s => order.tracking?.find(t => t.label === s.label)?.completed).length;
                  const progressHeight = isTerminated ? "100%" : `${(completedCount / allTrackingSteps.length) * 100}%`;
                  return (
                    <Box sx={{ position: "absolute", left: 20, top: 0, bottom: 0, width: 4, bgcolor: colors.surfaceContainerHigh, borderRadius: 2 }}>
                      <Box sx={{ position: "absolute", top: 0, left: 0, width: "100%", bgcolor: isTerminated ? colors.error : colors.successGreen, borderRadius: 2, height: progressHeight }} />
                    </Box>
                  );
                })()}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {(() => {
                  const isRejected = order.tracking?.some(t => t.key === "VENDOR_REJECTED");
                  const cancelledTrack = order.tracking?.find(t => t.key === "CANCELLED");
                  const isCancelled = !!cancelledTrack;

                  // Build display steps dynamically — start with base steps
                  let displaySteps = [...allTrackingSteps];

                  if (isCancelled) {
                    // Find last completed step (the point before cancellation)
                    const lastCompletedIdx = displaySteps.reduce((last, step, i) => {
                      const track = order.tracking?.find(t => t.label === step.label);
                      return track?.completed ? i : last;
                    }, -1);

                    // Insert CANCELLED after the last completed step
                    const cancelStep = {
                      label: "Order Cancelled by Vendor",
                      key: "CANCELLED",
                    };
                    displaySteps.splice(lastCompletedIdx + 1, 0, cancelStep);

                    // Truncate: keep only up to and including CANCELLED
                    displaySteps = displaySteps.slice(0, lastCompletedIdx + 2);
                  }

                  if (isRejected) {
                    // Find VENDOR_REJECTED index
                    const rejectIdx = displaySteps.findIndex(s => s.key === "VENDOR_REJECTED");
                    if (rejectIdx >= 0) {
                      // Truncate: keep only up to and including VENDOR_REJECTED
                      displaySteps = displaySteps.slice(0, rejectIdx + 1);
                    }
                  }

                  return displaySteps.map((step) => {
                    // Skip VENDOR_REJECTED if order was not rejected
                    if (step.key === "VENDOR_REJECTED" && !isRejected) return null;
                    // Skip CANCELLED if order was not cancelled
                    if (step.key === "CANCELLED" && !isCancelled) return null;
                    const track = order.tracking?.find(t => t.label === step.label);
                    const isCompleted = track?.completed === true;
                    const isRejectStep = step.key === "VENDOR_REJECTED" && isCompleted;
                    const isCancelStep = step.key === "CANCELLED" && isCompleted;
                    return (
                      <Box key={step.key} sx={{ display: "flex", alignItems: "flex-start", gap: 2.5, pl: 2, position: "relative" }}>
                        <Box sx={{ position: "absolute", left: 13, top: 4, width: 22, height: 22, borderRadius: "50%", bgcolor: isRejectStep || isCancelStep ? colors.error : isCompleted ? colors.successGreen : colors.surfaceContainerHigh, border: "3px solid", borderColor: colors.surfaceContainerLowest, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1 }}>
                          {isCompleted && (isRejectStep || isCancelStep ? <CloseIcon sx={{ fontSize: 14, color: "#fff" }} /> : <CheckCircleIcon sx={{ fontSize: 14, color: "#fff" }} />)}
                        </Box>
                        <Box sx={{ ml: 4 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: 14, color: isRejectStep || isCancelStep ? colors.error : isCompleted ? colors.onSurface : colors.textMuted }}>
                            {step.label}
                          </Typography>
                          {isCompleted && track?.time ? (
                            <Typography sx={{ fontSize: 12, color: colors.textMuted, mt: 0.25 }}>
                              {new Date(track.time).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}
                            </Typography>
                          ) : (
                            <Typography sx={{ fontSize: 12, color: colors.textMuted, fontStyle: "italic", mt: 0.25 }}>Pending</Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  });
                })()}
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Right Column */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Delivery Agent */}
          {!isCancelled && !isRejected && (
          <Paper sx={{ p: 3, borderRadius: 1, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", border: "1px solid", borderColor: colors.outlineVariant }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: colors.onSurfaceVariant, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Delivery Agent
              </Typography>
              <Chip
                label={order.agent?.status === "ASSIGNED" || order.agent?.status === "ACTIVE" || order.agent?.status === "COMPLETED" ? "ASSIGNED" : "UNASSIGNED"}
                size="small"
                sx={{ fontWeight: 600, fontSize: 11, bgcolor: order.agent?.status === "ASSIGNED" || order.agent?.status === "ACTIVE" || order.agent?.status === "COMPLETED" ? "rgba(56,142,60,0.1)" : colors.errorContainer, color: order.agent?.status === "ASSIGNED" || order.agent?.status === "ACTIVE" || order.agent?.status === "COMPLETED" ? colors.successGreen : colors.onErrorContainer }}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2, borderTop: "1px dashed", borderBottom: "1px dashed", borderColor: colors.outlineVariant, my: 2 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: "50%", bgcolor: colors.surfaceContainerHigh, overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {order.agent?.profilePicUrl ? (
                  <img
                    src={order.agent.profilePicUrl}
                    alt={order.agent.name || "Agent"}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <PersonIcon sx={{ color: colors.textMuted, fontSize: 28 }} />
                )}
              </Box>
              <Box>
                {order.agent?.status === "ASSIGNED" || order.agent?.status === "ACTIVE" || order.agent?.status === "COMPLETED" ? (
                  <>
                    <Typography sx={{ fontSize: 13, color: colors.textMuted }}>Agent: {order.agent.name || "Assigned"}</Typography>
                    {(() => {
                      const a = order.agent.assignedAt ? new Date(order.agent.assignedAt) : null;
                      return (
                        <>
                          <Typography sx={{ fontSize: 12, color: colors.textMuted, textTransform: "uppercase", letterSpacing: "0.5px", mt: 0.5 }}>
                            Assigned At
                          </Typography>
                          <Typography sx={{ fontWeight: 600, fontSize: 14, color: colors.onSurface, mt: 0.25 }}>
                            {a
                              ? `${a.toLocaleDateString("en-IN", {
                                  weekday: "long",
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                })}, ${a
                                  .toLocaleTimeString("en-IN", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    second: "2-digit",
                                    hour12: true,
                                  })
                                  .toUpperCase()}`
                              : "TBD"}
                          </Typography>
                        </>
                      );
                    })()}
                  </>
                ) : (
                  <>
                    <Typography sx={{ fontSize: 13, color: colors.textMuted }}>Waiting for allocation...</Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: 14, color: colors.onSurface }}>Agent details will appear here</Typography>
                  </>
                )}
              </Box>
            </Box>
            {(() => {
              const est = order.estimatedDeliveryDate
                ? new Date(order.estimatedDeliveryDate)
                : null;
              return (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: 12, color: colors.textMuted, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Estimated Delivery Date
                  </Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: 15, color: est ? colors.onSurface : colors.textMuted, mt: 0.5 }}>
                    {est
                      ? est.toLocaleDateString("en-IN", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                      : "Pending"}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: colors.textMuted, mt: 0.5 }}>
                    A combination of vendor preparation time + agent delivery time.
                  </Typography>
                </Box>
              );
            })()}
            {(() => {
              const d = order.deliveryPhase?.deliveredAt
                ? new Date(order.deliveryPhase.deliveredAt)
                : null;
              return (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: 12, color: colors.textMuted, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Actual Delivery Date
                  </Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: 15, color: d ? colors.onSurface : colors.textMuted, mt: 0.5 }}>
                    {d
                      ? `${d.toLocaleDateString("en-IN", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}, ${d
                          .toLocaleTimeString("en-IN", {
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                            hour12: true,
                          })
                          .toUpperCase()}`
                      : "Pending"}
                  </Typography>
                </Box>
              );
            })()}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <InfoIcon sx={{ fontSize: 14, color: colors.textMuted }} />
              <Typography sx={{ fontSize: 12, color: colors.textMuted }}>Assignment usually happens 1-2 hours after order is accepted by the Vendor.</Typography>
            </Box>
          </Paper>
          )}

          {/* Shipping Address */}
          {order.shippingAddress && (
            <Paper sx={{ p: 3, borderRadius: 1, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", border: "1px solid", borderColor: colors.outlineVariant }}>
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: colors.onSurfaceVariant, textTransform: "uppercase", letterSpacing: "0.5px", mb: 2 }}>
                Shipping Address
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: 14, color: colors.onSurface }}>
                {order.shippingAddress.fullName || order.shippingAddress.name || "Customer"}
              </Typography>
              <Typography sx={{ fontSize: 14, color: colors.onSurfaceVariant, mt: 1, lineHeight: 1.6 }}>
                {order.shippingAddress.addressLine1}<br />
                {order.shippingAddress.addressLine2 && <>{order.shippingAddress.addressLine2}<br /></>}
                {order.shippingAddress.city}{order.shippingAddress.state ? `, ${order.shippingAddress.state}` : ""} {order.shippingAddress.pincode || ""}<br />
                {order.shippingAddress.country || "India"}
              </Typography>
              {order.shippingAddress.phone && (
                <Typography sx={{ fontSize: 14, color: colors.onSurfaceVariant, mt: 1.5, fontWeight: 600 }}>
                  Phone: {order.shippingAddress.phone}
                </Typography>
              )}
            </Paper>
          )}

          {/* Route Map */}
          {!isCancelled && !isRejected && (
          <Paper sx={{ p: 3, borderRadius: 1, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", border: "1px solid", borderColor: colors.outlineVariant }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: colors.onSurfaceVariant, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Route Map
              </Typography>
              {(() => {
                let mapsUrl = null;
                if (pickupLocation && dropoffLocation) {
                  mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${pickupLocation.lat},${pickupLocation.lng}&destination=${dropoffLocation.lat},${dropoffLocation.lng}`;
                } else if (pickupAddressText && dropoffAddressText) {
                  mapsUrl = `https://www.google.com/maps/dir/${encodeURIComponent(pickupAddressText)}/${encodeURIComponent(dropoffAddressText)}`;
                }
                return mapsUrl ? (
                  <Button
                    component="a"
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{ textTransform: "none", fontWeight: 600, fontSize: 12, minWidth: "auto", color: colors.primary, "&:hover": { textDecoration: "underline" } }}
                  >
                    <OpenInNewIcon sx={{ fontSize: 14, mr: 0.5 }} /> Open in Maps
                  </Button>
                ) : null;
              })()}
            </Box>
            <RouteMap pickupAddress={pickupAddressText} dropoffAddress={dropoffAddressText} pickupCoords={pickupLocation} dropoffCoords={dropoffLocation} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1.5, justifyContent: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: colors.primary, flexShrink: 0 }} /><Typography variant="caption" sx={{ color: colors.textMuted }}>Pickup (P)</Typography></Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: colors.successGreen, flexShrink: 0 }} /><Typography variant="caption" sx={{ color: colors.textMuted }}>Dropoff (D)</Typography></Box>
            </Box>
          </Paper>
          )}

          {/* Order Summary */}
          <Paper sx={{ p: 3, borderRadius: 1, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", border: "1px solid", borderColor: colors.outlineVariant }}>
            <Typography sx={{ fontWeight: 600, fontSize: 13, color: colors.onSurfaceVariant, textTransform: "uppercase", letterSpacing: "0.5px", mb: 3 }}>
              Order Summary
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontSize: 14, color: colors.onSurfaceVariant }}>Items:</Typography>
                <Typography sx={{ fontSize: 14, color: colors.onSurface, fontWeight: 600 }}>₹{Number(order.total || 0).toLocaleString("en-IN")}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontSize: 14, color: colors.onSurfaceVariant }}>Shipping & Handling:</Typography>
                <Typography sx={{ fontSize: 14, color: colors.onSurface, fontWeight: 600 }}>₹0.00</Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 2, borderColor: colors.outlineVariant }} />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography sx={{ fontWeight: 600, fontSize: 16, color: colors.onSurface }}>Order Total:</Typography>
              <Typography sx={{ fontWeight: 700, fontSize: 16, color: colors.onSurface }}>₹{Number(order.total || 0).toLocaleString("en-IN")}</Typography>
            </Box>
            <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Button fullWidth variant="contained"
                onClick={() => navigate("/customer/dashboard/orders")}
                sx={{ bgcolor: colors.secondaryContainer, color: colors.deepNavy, textTransform: "none", fontWeight: 600, "&:hover": { bgcolor: colors.warningAmber } }}>
                Back to Orders
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
    <ReviewDialog
      open={openReviewDialog}
      onClose={() => { setOpenReviewDialog(false); setEditingReviewId(null); }}
      productId={firstItem?.productId || firstItem?._id || firstItem?.id}
      orderId={order?._id}
      mode={editingReviewId ? "EDIT" : "WRITE"}
      reviewId={editingReviewId}
      onSuccess={() => setNotification({ type: "success", message: "Review submitted" })}
    />
    </>
  );
};

export default OrderDetailsDashboard;